import { useLayoutEffect, useMemo, useRef } from 'react';
import { RowCheckbox, SelectAllCheckbox } from './SelectionControls';
import type { DataTableColumn, DataTableSelection } from './types';

/** The selection feature, packaged so `DataTable`'s body stays lean. */
export function useSelectionColumn<T, K extends string>(
    selection: DataTableSelection<T> | undefined,
    rows: readonly T[],
    getRowKey: (row: T, index: number) => string | number
) {
    // Latest-refs: handlers fire at event time, and getRowKey must be a pure
    // row->key derivation, so reading both through refs is safe.
    const latestSelection = useRef(selection);
    const latestGetRowKey = useRef(getRowKey);
    useLayoutEffect(() => {
        latestSelection.current = selection;
        latestGetRowKey.current = getRowKey;
    });

    // Per-render check; only its OUTPUT (a class string) reaches the row memo.
    const isRowSelected = (row: T, index: number): boolean => {
        if (!selection?.enabled) return false;
        const key = getRowKey(row, index);
        return selection.mode === 'single'
            ? selection.selectedKey === key
            : selection.selectedKeys.has(key);
    };

    // Stable event-time toggle; false when selection is off so callers fall
    // through to their own row action.
    const toggleSelection = useRef((row: T, index: number): boolean => {
        const sel = latestSelection.current;
        if (!sel?.enabled) return false;
        if (sel.mode === 'single') {
            const key = latestGetRowKey.current(row, index);
            sel.onSelect(sel.selectedKey === key ? null : row);
        } else {
            sel.onToggleRow(row, index);
        }
        return true;
    }).current;

    // The selection state, narrowed to memo-safe dependency values.
    const enabled = selection?.enabled === true;
    const mode = selection?.enabled && selection.mode === 'single' ? 'single' : 'multiple';
    const multiKeys =
        selection?.enabled && selection.mode !== 'single' ? selection.selectedKeys : undefined;
    const singleKey =
        selection?.enabled && selection.mode === 'single' ? selection.selectedKey : null;

    // Memoized so enabling selection cannot defeat DataTableBodyRow's memo:
    // identity moves only with the selected set, the mode or the rows.
    const selectionColumn = useMemo((): DataTableColumn<T, K> | null => {
        if (!enabled) return null;
        const isSelected = (row: T, index: number): boolean => {
            const key = latestGetRowKey.current(row, index);
            return mode === 'single' ? singleKey === key : (multiKeys?.has(key) ?? false);
        };
        const selectedCount = rows.reduce(
            (count, row, index) => count + (isSelected(row, index) ? 1 : 0),
            0
        );
        return {
            key: '__selection',
            label: 'Select',
            width: '48px',
            cellClassName: 'px-0',
            headerClassName: 'px-0',
            stopClickPropagation: true,
            // Single mode has no select-all semantics - blank visible header.
            headerNode:
                mode === 'single' ? null : (
                    <SelectAllCheckbox
                        state={
                            rows.length > 0 && selectedCount === rows.length
                                ? 'all'
                                : selectedCount > 0
                                  ? 'some'
                                  : 'none'
                        }
                        onToggle={() => {
                            const sel = latestSelection.current;
                            if (sel?.enabled && sel.mode !== 'single') sel.onToggleAll();
                        }}
                    />
                ),
            render: (row, index) => (
                <RowCheckbox
                    checked={isSelected(row, index)}
                    onToggle={() => toggleSelection(row, index)}
                />
            )
        };
    }, [enabled, mode, multiKeys, singleKey, rows, toggleSelection]);

    const selectedTotal = !selection?.enabled
        ? 0
        : selection.mode === 'single'
          ? selection.selectedKey === null
              ? 0
              : 1
          : selection.selectedKeys.size;

    return { selectionColumn, isRowSelected, toggleSelection, selectedTotal };
}
